import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { WebSearchToggle } from './WebSearchToggle';

export const ChatInput = ({ 
  onSend, 
  disabled = false, 
  webSearchEnabled, 
  onWebSearchToggle,
  isSearching = false 
}) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef(null);

  // Auto-resize textarea
  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [message, resizeTextarea]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSend(message.trim());
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  }, [message, disabled, onSend]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  }, [handleSubmit]);

  const handleChange = useCallback((e) => {
    setMessage(e.target.value);
  }, []);

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="input-capsule flex items-end gap-2 p-2 pl-4">
        <WebSearchToggle 
          enabled={webSearchEnabled} 
          onToggle={onWebSearchToggle}
          isSearching={isSearching}
        />
        
        <div className="flex-1 flex items-end">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Message NYXT AI..."
            disabled={disabled}
            data-testid="chat-input"
            className="
              w-full resize-none border-0 bg-transparent py-2 px-2
              text-[15px] leading-relaxed
              placeholder:text-[#8A908A]
              focus:outline-none focus:ring-0
              disabled:opacity-50
            "
            style={{ 
              color: '#1A1D1A',
              minHeight: '24px',
              maxHeight: '200px'
            }}
            rows={1}
          />
        </div>
        
        <Button
          type="submit"
          disabled={!message.trim() || disabled}
          data-testid="send-message-btn"
          className="
            rounded-full w-10 h-10 p-0 flex-shrink-0
            bg-[#D97757] hover:bg-[#c5664a] 
            text-white
            disabled:opacity-40 disabled:cursor-not-allowed
            transition-all duration-200
            btn-lift
          "
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </form>
  );
};

export default ChatInput;
